import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle, Check, Loader, ChevronDown } from 'lucide-react';

export function AuthHeading({ eyebrow, title, subtitle, align = 'left' }) {
  return (
    <div style={{ marginBottom: 32, textAlign: align }}>
      {eyebrow && (
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748B', marginBottom: 12 }}>
          {eyebrow}
        </div>
      )}
      <h1 style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.05, margin: 0, color: '#000', textWrap: 'balance' }}>{title}</h1>
      {subtitle && (
        <p style={{ fontSize: 15, lineHeight: 1.5, margin: '12px 0 0', color: '#64748B', textWrap: 'pretty' }}>{subtitle}</p>
      )}
    </div>
  );
}

export function AuthField({ label, optional, error, hint, children, htmlFor, style: extraStyle = {} }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...extraStyle }}>
      {label && (
        <label htmlFor={htmlFor} style={{ fontSize: 13, fontWeight: 500, color: '#000', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span>{label}</span>
          {optional && <span style={{ fontWeight: 400, color: '#64748B', fontSize: 12 }}>Optional</span>}
        </label>
      )}
      {children}
      {(error || hint) && (
        <span style={{ fontSize: 12, color: error ? '#FF0004' : '#64748B', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {error && <AlertCircle size={12} />}
          {error || hint}
        </span>
      )}
    </div>
  );
}

export function AuthInput({ id, error, leadingIcon: LeadingIcon, trailingIcon: TrailingIcon, trailing, style: extraStyle = {}, ...rest }) {
  const [focused, setFocused] = useState(false);
  const border = error ? '#FF0004' : '#000';
  const hasTrailing = !!(TrailingIcon || trailing);
  return (
    <div style={{ position: 'relative', border: `${focused ? 2 : 1}px solid ${border}`, background: error ? '#FFF5F5' : '#fff', transition: 'border 0.1s', ...extraStyle }}>
      {LeadingIcon && (
        <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#64748B', display: 'inline-flex', pointerEvents: 'none' }}>
          <LeadingIcon size={16} />
        </span>
      )}
      <input
        id={id}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          display: 'block',
          width: '100%',
          boxSizing: 'border-box',
          paddingTop: focused ? 11 : 12,
          paddingBottom: focused ? 11 : 12,
          paddingLeft: (LeadingIcon ? 42 : (focused ? 15 : 16)),
          paddingRight: (hasTrailing ? 42 : (focused ? 15 : 16)),
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 14,
          color: '#000',
          fontFamily: 'inherit',
        }}
        {...rest}
      />
      {(TrailingIcon || trailing) && (
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748B', display: 'inline-flex' }}>
          {TrailingIcon ? <TrailingIcon size={16} /> : trailing}
        </span>
      )}
    </div>
  );
}

export function AuthSelect({ id, error, value, onChange, children, placeholder, style: extraStyle = {} }) {
  const [focused, setFocused] = useState(false);
  const border = error ? '#FF0004' : '#000';
  const empty = value === '' || value == null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', position: 'relative', height: 47, boxSizing: 'border-box', border: `${focused ? 2 : 1}px solid ${border}`, background: error ? '#FFF5F5' : '#fff', padding: focused ? '0 15px' : '0 16px', transition: 'border 0.1s', ...extraStyle }}>
      <select
        id={id}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: 14, lineHeight: 'normal', color: empty ? '#A0ABBB' : '#000', minWidth: 0, padding: 0, appearance: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {children}
      </select>
      <span style={{ color: '#64748B', display: 'inline-flex', pointerEvents: 'none' }}><ChevronDown size={16} /></span>
    </div>
  );
}

export function AuthTextarea({ id, error, style: extraStyle = {}, rows = 3, ...rest }) {
  const [focused, setFocused] = useState(false);
  const border = error ? '#FF0004' : '#000';
  return (
    <textarea
      id={id}
      rows={rows}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', border: `${focused ? 2 : 1}px solid ${border}`, background: error ? '#FFF5F5' : '#fff', padding: focused ? '11px 15px' : '12px 16px', fontSize: 14, color: '#000', outline: 'none', transition: 'border 0.1s', lineHeight: 1.5, fontFamily: 'inherit' }}
      {...rest}
    />
  );
}

export function PasswordInput({ id, value, onChange, error, autoComplete = 'current-password', placeholder = '' }) {
  const [show, setShow] = useState(false);
  return (
    <AuthInput
      id={id}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      placeholder={placeholder}
      error={error}
      trailing={
        <button type="button" onClick={() => setShow(s => !s)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#64748B', display: 'inline-flex' }} aria-label={show ? 'Hide password' : 'Show password'}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      }
    />
  );
}

function calcStrength(pw) {
  if (!pw) return { score: 0, label: '' };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const label = s <= 1 ? 'Weak' : s === 2 ? 'Fair' : s === 3 ? 'Good' : 'Strong';
  return { score: Math.min(s, 4), label };
}

export function PasswordStrength({ value }) {
  const { score, label } = calcStrength(value);
  if (!value) return null;
  const color = score <= 1 ? '#FF0004' : score === 2 ? '#B7791F' : score === 3 ? '#1F8A4C' : '#000';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
      <div style={{ display: 'flex', gap: 3, flex: 1 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ height: 4, flex: 1, background: i < score ? color : '#E5E7EB' }} />
        ))}
      </div>
      <span style={{ fontSize: 11, color: '#64748B', minWidth: 44, textAlign: 'right' }}>{label}</span>
    </div>
  );
}

export function ErrorBanner({ children }) {
  if (!children) return null;
  return (
    <div style={{ border: '1px solid #FF0004', background: '#FFF5F5', color: '#000', padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.45, marginBottom: 16 }}>
      <span style={{ color: '#FF0004', flexShrink: 0, marginTop: 1 }}><AlertCircle size={16} /></span>
      <span>{children}</span>
    </div>
  );
}

export function CheckBox({ checked, onChange, label, error }) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, lineHeight: 1.5, color: '#000' }}>
      <span onClick={e => { e.preventDefault(); onChange?.(!checked); }} style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0, border: `1px solid ${error ? '#FF0004' : '#000'}`, background: checked ? '#000' : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {checked && <Check size={12} style={{ color: '#fff' }} />}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
    </label>
  );
}

export function PrimaryButton({ children, loading, disabled, full = true, icon: Icon, ...rest }) {
  return (
    <button
      type={rest.type || 'button'}
      disabled={disabled || loading}
      style={{ fontSize: 14, fontWeight: 600, background: '#000', color: '#fff', border: '1px solid #000', padding: '14px 20px', cursor: disabled || loading ? 'not-allowed' : 'pointer', opacity: disabled || loading ? 0.4 : 1, width: full ? '100%' : 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'opacity .15s', fontFamily: 'inherit' }}
      onMouseOver={e => { if (!disabled && !loading) e.currentTarget.style.opacity = '0.85'; }}
      onMouseOut={e => { if (!disabled && !loading) e.currentTarget.style.opacity = '1'; }}
      {...rest}
    >
      {loading ? <Loader size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : (Icon && <Icon size={16} />)}
      {loading ? 'Working…' : children}
    </button>
  );
}
